import React, { ReactNode } from 'react';
import Skeleton from 'react-loading-skeleton';

import { Loading } from '@/src/globalComponents';
import { cn } from '@/src/util/ui';

export const TableLayout = ({
  data,
  columns,
  containerClassName,
  columnProps,
  children,
  actionColumns = [],
  showHeader = true,
  headerRowClassName = null,
  commonHeaderCellClassName = null,
  bodyClassName = null,
  isPrefilled,
}) => {
  return (
    <div
      className={cn(
        'table-container',
        'tw-m-0',
        'tw-border-[1px] tw-border-solid tw-border-cfo-grey-500',
        containerClassName
      )}
    >
      {showHeader && (
        <div className={cn('table-header', 'tw-sticky tw-top-0 tw-z-[1]', 'tw-bg-white')}>
          <div
            className={cn(
              'table-row',
              'tw-flex tw-items-center',
              'tw-border-b-[1px] tw-border-solid tw-border-cfo-grey-500',
              isPrefilled && 'tw-gap-x-[10px]',
              headerRowClassName
            )}
          >
            {columns.map(
              (
                {
                  title,
                  HeaderCell,
                  customClasses,
                  paddingClasses,
                  headerCellClassName,
                  textAlignment = 'text-left',
                  width = '25%',
                  isShowColumn = true,
                },
                index
              ) =>
                isShowColumn && (
                  <div
                    className={cn(
                      'row-item',
                      'tw-font-brandonTextMedium tw-text-cfo-black tw-font-normal',
                      paddingClasses
                        ? paddingClasses
                        : 'tw-py-[10px] tw-pl-[10px] first:tw-pl-[20px] last:tw-pr-[20px]',
                      commonHeaderCellClassName,
                      headerCellClassName,
                      textAlignment,
                      customClasses
                    )}
                    style={{ width: width }}
                    key={'thr' + index}
                  >
                    {HeaderCell ? <HeaderCell data={data} {...columnProps} /> : title}
                  </div>
                )
            )}
            {actionColumns.map(
              (
                {
                  HeaderCell,
                  customClasses,
                  paddingClasses,
                  headerCellClassName,
                  id,
                  width = '65px',
                },
                index
              ) => (
                <div
                  className={cn(
                    'row-item',
                    'tw-font-brandonTextMedium tw-text-cfo-black tw-font-normal',
                    paddingClasses
                      ? paddingClasses
                      : 'tw-py-[10px] tw-pl-[10px] first:tw-pl-[20px] last:tw-pr-[20px]',
                    commonHeaderCellClassName,
                    headerCellClassName,
                    customClasses
                  )}
                  style={{ width: width }}
                  data-id={`thr_ac-${id || index}`}
                  key={`thr_ac-${id || index}`}
                >
                  <HeaderCell data={data} {...columnProps} />
                </div>
              )
            )}
          </div>
        </div>
      )}
      <div className={cn('table-body', bodyClassName)}>{children}</div>
    </div>
  );
};

const TableRowSkeleton = ({
  columns = [],
  rowCount = 0,
  bodyRowClassName = '',
  rowClassName = '',
}: {
  rowCount: number;
  columns: TableProps['columns'];
  bodyRowClassName: TableProps['bodyRowClassName'];
  rowClassName: TableProps['rowClassName'];
}) => {
  return (
    <>
      {[...Array(rowCount).keys()].map((i) => (
        <div
          className={cn(
            'table-row',
            'tw-flex tw-items-center',
            'tw-border-b-[1px] tw-border-solid tw-border-cfo-grey-500',
            rowClassName
          )}
          key={`table-row_${i}`}
        >
          {columns.map(
            (
              { textAlignment, customClasses, paddingClasses, width = '25%', isShowColumn = true },
              columnIndex
            ) =>
              isShowColumn && (
                <div
                  className={cn(
                    'row-item',
                    bodyRowClassName,
                    paddingClasses
                      ? paddingClasses
                      : 'tw-py-[10px] tw-px-[10px] first:tw-pl-[20px] last:tw-pr-[20px]',
                    textAlignment,
                    customClasses
                  )}
                  style={{ width: width }}
                  key={'tr' + i + columnIndex}
                >
                  <Skeleton width={'100%'} />
                </div>
              )
          )}
        </div>
      ))}
    </>
  );
};

export interface TableProps {
  columns: {
    [key: string | number]: any;
  }[];
  data: {
    [key: string | number]: any;
  }[];
  children?: ReactNode;
  actionColumns?: {
    [key: string | number]: any;
  }[];
  rowProps?: object;
  columnProps?: object;
  containerClassName?: string;
  rowClassName?: string;
  headerRowClassName?: string;
  bodyRowClassName?: string;
  commonHeaderCellClassName?: string;
  bodyClassName?: string;
  showHeader?: boolean;
  isLoadingNextPage?: boolean;
  isLoading?: boolean;
  isUseSkeletonLoading?: boolean;
  skeletonLoadingRowCount?: number;
  childrenPosition?: 'top' | 'bottom';
  onClickRow?: (data: any) => void;
  isPrefilled?: boolean;
}

const Table = ({
  columns,
  data = [],
  actionColumns = [],
  children = null,
  rowProps = {},
  columnProps = {},
  onClickRow = null,
  containerClassName = null,
  headerRowClassName = null,
  rowClassName = null,
  bodyRowClassName = null,
  commonHeaderCellClassName = null,
  bodyClassName = null,
  showHeader = true,
  isLoading = false,
  isLoadingNextPage = false,
  isUseSkeletonLoading = false,
  skeletonLoadingRowCount = 0,
  childrenPosition = 'bottom',
  isPrefilled = false,
}: TableProps) => {
  const myColumns: TableProps['columns'] = Array.isArray(columns)
    ? columns
    : Object.values(columns);

  return (
    <TableLayout
      // key={`${myColumns.length}_tl`}
      columns={myColumns}
      actionColumns={actionColumns}
      columnProps={columnProps}
      headerRowClassName={headerRowClassName}
      containerClassName={containerClassName}
      data={data}
      commonHeaderCellClassName={commonHeaderCellClassName}
      bodyClassName={bodyClassName}
      showHeader={showHeader}
      isPrefilled={isPrefilled}
    >
      {children && childrenPosition === 'top' && children}
      {!isUseSkeletonLoading && isLoading && <Loading />}
      {isUseSkeletonLoading && isLoading && (
        <TableRowSkeleton
          rowCount={skeletonLoadingRowCount}
          rowClassName={rowClassName}
          bodyRowClassName={bodyRowClassName}
          columns={myColumns}
        />
      )}
      {!isLoading &&
        data.map((entry, entryIndex) => {
          const entryId = entry && entry.id ? entry.id : entryIndex;
          return (
            <div
              className={cn(
                'table-row',
                'tw-flex tw-items-center',
                'tw-border-b-[1px] tw-border-solid tw-border-cfo-grey-500',
                rowClassName,
                isPrefilled && 'tw-gap-x-[10px]'
              )}
              key={`table-row_${entryId}`}
              onClick={onClickRow ? onClickRow.bind(null, entry) : null}
              onKeyUp={onClickRow ? onClickRow.bind(null, entry) : null}
              role="button"
              tabIndex={0}
            >
              {myColumns.map(
                (
                  {
                    Cell,
                    field,
                    textAlignment,
                    customClasses,
                    paddingClasses,
                    width = '25%',
                    isShowColumn = true,
                  },
                  columnIndex
                ) =>
                  isShowColumn && (
                    <div
                      className={cn(
                        'row-item',
                        ' tw-text-cfo-black tw-font-normal',
                        'tw-break-words',
                        bodyRowClassName,
                        paddingClasses
                          ? paddingClasses
                          : 'tw-py-[10px] tw-pl-[10px] first:tw-pl-[20px] last:tw-pr-[20px]',
                        textAlignment,
                        customClasses
                      )}
                      style={{ width: width }}
                      key={'tr' + entryId + columnIndex}
                    >
                      {Cell ? (
                        <Cell entry={entry} rowIndex={entryIndex} {...rowProps} />
                      ) : (
                        entry[field]
                      )}
                    </div>
                  )
              )}
              {actionColumns.map(
                ({ textAlignment, customClasses, paddingClasses, width = '65px' }, columnIndex) => (
                  <div
                    className={cn(
                      'row-item',
                      ' tw-text-cfo-black tw-font-normal',
                      'tw-break-words',
                      paddingClasses
                        ? paddingClasses
                        : 'tw-py-[10px] tw-pl-[10px] first:tw-pl-[20px] last:tw-pr-[20px]',
                      bodyRowClassName,
                      textAlignment,
                      customClasses
                    )}
                    style={{ width: width }}
                    key={'tr' + entryId + columnIndex}
                  >
                    <span></span>
                  </div>
                )
              )}
            </div>
          );
        })}
      {isUseSkeletonLoading && isLoadingNextPage && (
        <TableRowSkeleton
          rowCount={3}
          rowClassName={rowClassName}
          bodyRowClassName={bodyRowClassName}
          columns={myColumns}
        />
      )}
      {children && childrenPosition === 'bottom' && children}
    </TableLayout>
  );
};

export default Table;
